import React, {
  useLayoutEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useFullLoader, useToast } from '../../../redux/actions/UI';
import { InvalidDataError } from '../api';
import {
  useCampaignQuery,
  useUpdateCampaignMutation,
  useAutosaveCampaignMutation,
} from '../queries';
import CampaignEditor from '../CampaignEditor';
import { ErrorMessage, LoadingMessage } from './EditCampaign.styled';
import NotFoundError from '../api/NotFoundError';
import useCampaignAutosave from '../hooks/useCampaignAutosave';
import { useCampaignEditorLogic } from '../hooks/useCampaignEditorLogic';

const useFullScreenSpinner = isVisible => {
  const fullLoader = useFullLoader();
  useLayoutEffect(() => {
    if (isVisible) {
      fullLoader.show('Loading campaign...');
      return fullLoader.hide;
    }
  }, [fullLoader, isVisible]);
};

const EditCampaign = () => {
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const fullLoader = useFullLoader();
  const { successToast, errorToast } = useToast();
  const { data, isLoading, isError, error } = useCampaignQuery(id, {
    // Prevent unnecessary refetches when navigating between steps
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh enough during editing
    refetchOnMount: false, // Don't refetch when component remounts
  });
  const { mutateAsync: updateCampaign } = useUpdateCampaignMutation();
  const { mutateAsync: autosaveCampaign } = useAutosaveCampaignMutation();
  // Check for step and stageId in query params to determine initial step
  // This ensures TemplateEditor opens when navigating to ?step=template&stageId=xxx
  const queryParams = queryString.parse(location.search);

  const initialStep = useMemo(() => {
    if (queryParams.step === 'template' && queryParams.stageId) {
      return {
        step: 'template/editor',
        params: { stage: queryParams.stageId },
      };
    }
    // step=audience
    if (queryParams.step === 'audience') {
      return {
        step: 'audience/editor',
        params: {},
      };
    }

    return { step: 'stages', params: {} };
  }, [queryParams.step, queryParams.stageId]);
  // Track current campaign data for autosave
  const [currentCampaignData, setCurrentCampaignData] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isPausedBannerDismissed, setIsPausedBannerDismissed] = useState(false);
  const isInitialLoadRef = useRef(true);
  const lastCampaignIdRef = useRef(null);
  // Store original status before editing starts (to restore on Review screen)
  const originalStatusRef = useRef(null);

  useFullScreenSpinner(isLoading);

  // Handle autosave errors
  // Note: This callback is currently unused as useCampaignAutosave treats all errors as successful saves
  // We always attempt to save changes, even if they are incomplete/invalid
  const handleAutosaveError = useCallback(
    error => {
      console.error('Autosave error:', error);
      // Don't show error toast for InvalidDataError - we always save regardless of validation
      // InvalidDataError represents validation issues, but we still save incomplete data
      if (!(error instanceof InvalidDataError)) {
        errorToast(
          'Failed to autosave campaign. Changes may be lost if you leave this page.',
          5
        );
      }
    },
    [errorToast]
  );

  // Setup autosave hook - use autosave mutation that doesn't invalidate queries
  const { saveNow, saveStatus, hasFailed } = useCampaignAutosave({
    currentData: currentCampaignData,
    campaignId: id,
    onSave: autosaveCampaign, // Use autosave mutation instead of regular update
    onError: handleAutosaveError,
    isDirty,
  });

  // Custom logic for EditCampaign: save if dirty after validation
  const onAfterValidation = useCallback(async () => {
    if (isDirty && currentCampaignData) {
      await saveNow();
      setIsDirty(false);
    }
  }, [isDirty, currentCampaignData, saveNow]);

  // Use shared editor logic hook
  const { handleBeforeStepChange } = useCampaignEditorLogic({
    saveNow,
    hasFailed,
    currentCampaignData,
    onAfterValidation,
  });

  // Handle data changes from CampaignEditor
  const handleDataChange = useCallback(data => {
    // Ensure status is set to 'unfinished' when editing an existing campaign
    // This applies both on initial load (for existing campaigns being edited)
    // and when user makes changes
    const updatedData = { ...data };

    // If we have an originalStatus, we're editing an existing campaign
    // Always set status to 'unfinished' to indicate it's being edited
    if (originalStatusRef.current) {
      updatedData.status = 'unfinished';
      updatedData.originalStatus = originalStatusRef.current;
    }

    setCurrentCampaignData(updatedData);
    // Don't mark as dirty on initial load - only when user makes changes
    if (!isInitialLoadRef.current) {
      setIsDirty(true);
    } else {
      // Mark initial load as complete after first data change
      isInitialLoadRef.current = false;
    }
  }, []);

  // Handle touch (user makes changes)
  const handleTouch = useCallback(() => {
    setIsDirty(true);
  }, []);

  // Extract only editable fields (exclude read-only metadata like createdAt, updatedAt)
  const initialData = useMemo(() => {
    if (!data) return {};
    const { status, name, description, audience, stages, _id } = data;
    // Reset initial load flag when campaign ID changes (switching campaigns)
    if (_id && _id !== lastCampaignIdRef.current) {
      isInitialLoadRef.current = true;
      lastCampaignIdRef.current = _id;
      // Store original status when loading a campaign for editing
      // Store the current status as original (even if it's 'unfinished' for new campaigns)
      originalStatusRef.current = status || 'active';
    }
    // Set status to 'unfinished' when editing starts (will be saved via autosave)
    // This ensures campaigns are saved as 'unfinished' while being edited
    return {
      status: 'unfinished',
      name,
      description,
      audience,
      stages,
      _id,
      originalStatus: originalStatusRef.current, // Pass original status for Review screen
    };
  }, [data]);

  const handleConfirm = async (campaignData, shouldTriggerConfetti = false) => {
    try {
      // Save any pending autosave changes first
      if (isDirty) {
        await saveNow();
      }

      await fullLoader.during('Updating the campaign...', async () => {
        await updateCampaign({ id, updates: campaignData });
        setIsDirty(false);
        successToast('Campaign successfully updated');
        // Pass confetti trigger flag through location state

        history.push('/dashboard/automation', {
          triggerConfetti: shouldTriggerConfetti,
        });
      });
    } catch (error) {
      console.error(error);
      // Don't show error toast for InvalidDataError - allow saving regardless of validation
      if (!(error instanceof InvalidDataError)) {
        errorToast(
          'Failed to update campaign. Please, try again later. If the issue persists, please contact us.',
          10
        );
      }
    }
  };

  if (isError) {
    if (error instanceof NotFoundError) {
      return (
        <ErrorMessage>
          Campaign with id #{id} is not found. Please, make sure that you are
          using the valid URL. If the issue persists, please contact us.
        </ErrorMessage>
      );
    }

    return (
      <ErrorMessage>
        Failed to load the campaign. Please, try again later. If the issue,
        persists, please contact us.
      </ErrorMessage>
    );
  }

  if (isLoading) {
    return <LoadingMessage>Loading...</LoadingMessage>;
  }

  return (
    <>
      <CampaignEditor
        initialData={initialData}
        confirmText="Finish"
        onConfirm={handleConfirm}
        onTouch={handleTouch}
        onDataChange={handleDataChange}
        onBeforeStepChange={handleBeforeStepChange}
        initialStep={initialStep}
        autosaveStatus={saveStatus}
        isPausedBannerDismissed={isPausedBannerDismissed}
        onPausedBannerDismissed={setIsPausedBannerDismissed}
      />
    </>
  );
};

export default EditCampaign;
